/* ============================================================================
 * AURALIGN — Custom Reviews Section (v2)
 * ----------------------------------------------------------------------------
 * Two-tier load:
 *   1. aggregate JSON (tiny) → headline 4.8/N + axes + distribution + synthesis
 *      render IMMEDIATELY (no dependency on the review list).
 *   2. full reviews JSON → fetched lazily when the list scrolls near view;
 *      rendered append-only (DocumentFragment + event delegation), paginated.
 * Both files are produced by scripts/build-reviews.mjs. Nothing is hardcoded.
 *
 * Model: every number on screen is computed. Headline is the GLOBAL aggregate
 * and never changes on filter; filtering a product shows the matching subset
 * with a "showing N reviews mentioning X" note. Set reviews (products.length>1)
 * appear under each product tab but are counted once globally.
 * ==========================================================================*/
(function () {
  'use strict';

  var MOUNT = document.getElementById('auralign-reviews');
  if (!MOUNT) return;

  var AGG_URL  = MOUNT.getAttribute('data-agg');
  var DATA_URL = MOUNT.getAttribute('data-src');
  var DEFAULT_FILTER = (MOUNT.getAttribute('data-product') || 'all').toLowerCase();
  var ASSET_BASE = (DATA_URL || '').split('?')[0].replace(/custom-reviews-data\.json$/, '');
  var PAGE_SIZE = 10;

  var PRODUCTS = [
    { key: 'all', label: 'All' }, { key: 'set', label: 'Set' }, { key: 'ring', label: 'Ring' },
    { key: 'bracelet', label: 'Bracelet' }, { key: 'necklace', label: 'Necklace' },
    { key: 'earrings', label: 'Earrings' }
  ];
  var AXES = [
    { key: 'looks', label: 'Looks' }, { key: 'feel', label: 'Feel' },
    { key: 'value', label: 'Value' }, { key: 'fit', label: 'Fit' }
  ];

  /* Distinct line icons for the auto-summary bullets — never repeat (owner
     feedback: the old design repeated the same emoji on every bullet). Assigned
     by index so the set stays distinct whether bullets are deterministic or LLM. */
  var BULLET_ICONS = [
    '<path d="M12 3a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11z"/><path d="M8.5 13L7 21l5-2.8L17 21l-1.5-8"/>',   // medal
    '<path d="M12 20S4.5 15.3 4.5 9.9A3.8 3.8 0 0 1 12 7a3.8 3.8 0 0 1 7.5 2.9C19.5 15.3 12 20 12 20z"/>',    // heart
    '<path d="M12 3l1.8 5.4L19.5 10l-5.7 1.6L12 17l-1.8-5.4L4.5 10l5.7-1.6z"/>',                              // sparkle
    '<path d="M6 3.5h12l3 5-9 11-9-11z"/><path d="M3 8.5h18M10 3.5L6 8.5l6 11 6-11-4-5"/>',                   // gem
    '<rect x="3" y="8" width="18" height="8" rx="1.3"/><path d="M7.5 8v3.4M12 8v4.4M16.5 8v3.4"/>',           // ruler / fit
    '<path d="M3.5 11.5V4.5h7l9.5 9.5-7 7z"/><circle cx="8" cy="9" r="1.4"/>'                                 // tag / value
  ];
  var ICO = {
    sparkle:  '<path d="M12 3l1.7 5.5L19.5 10l-5.8 1.5L12 17l-1.7-5.5L4.5 10l5.8-1.5z"/>',
    star:     '<path d="M12 2.6l2.8 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17l-5.7 3 1.1-6.3L2.8 9.2l6.3-.9z"/>',
    play:     '<path d="M8 5.5v13l11-6.5z"/>',
    verified: '<circle cx="12" cy="12" r="9"/><path d="M8.4 12.3l2.4 2.4 4.8-5.4"/>'
  };
  function svgLine(inner) { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>'; }
  function svgFill(inner) { return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' + inner + '</svg>'; }

  var state = { agg: null, all: null, mediaIndex: null, filter: DEFAULT_FILTER, sort: 'best', star: null, topic: null, page: 1, listLoaded: false };

  /* ---- utils ------------------------------------------------------------ */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function assetUrl(f) { return !f ? '' : (/^(https?:)?\/\//.test(f) ? f : ASSET_BASE + f); }
  // localStorage can THROW on access (Safari Private mode, blocked cookies, quota) —
  // and these run inside the per-card render loop, so an unguarded throw would kill
  // the whole list. Treat unavailable storage as "no vote recorded".
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }
  function round1(n) { return Math.round(n * 10) / 10; }
  function prodLabel(key) { var p = PRODUCTS.find(function (x) { return x.key === key; }); return p ? p.label : key; }

  function timeAgo(iso) {
    var t = new Date(iso); if (isNaN(t)) return '';
    var d = Math.floor((Date.now() - t) / 86400000);
    if (d < 1) return 'today'; if (d < 7) return d + 'd ago';
    if (d < 30) { var w = Math.round(d / 7); return w + 'w ago'; }
    if (d < 365) { var m = Math.round(d / 30); return m + 'mo ago'; }
    return Math.round(d / 365) + 'y ago';
  }

  function stars(value, size) {
    var full = Math.round(value);
    var o = '<span class="alr-stars" role="img" aria-label="' + round1(value) + ' out of 5"' +
            (size ? ' style="--star:' + size + 'px"' : '') + '>';
    for (var i = 1; i <= 5; i++)
      o += '<svg class="alr-star' + (i <= full ? ' on' : '') + '" viewBox="0 0 20 20" aria-hidden="true">' +
           '<path d="M10 1.6l2.47 5.01 5.53.8-4 3.9.94 5.51L10 14.2l-4.95 2.6.94-5.5-4-3.9 5.53-.81z"/></svg>';
    return o + '</span>';
  }

  /* ---- data helpers ----------------------------------------------------- */
  function forFilter(list) {
    return list.filter(function (r) {
      // 'set' = multi-piece reviews; a piece tab = single-piece reviews of that
      // piece only (sets live under 'set', never a piece) — mirrors build by_product.
      if (state.filter === 'set') { if (!r.is_set) return false; }
      else if (state.filter !== 'all') { if (r.is_set || r.products[0] !== state.filter) return false; }
      if (state.star && Math.round(r.overall) !== state.star) return false;
      if (state.topic && (!r.topics || r.topics.indexOf(state.topic) === -1)) return false;
      return true;
    });
  }
  function anyFilterActive() { return state.filter !== 'all' || state.star || state.topic; }
  // Overview scopes to the active product tab: on a PDP (data-product set) the
  // headline/score/distribution/axes reflect THAT product; on 'all' (homepage /
  // reviews page) they reflect the whole catalogue.
  function activeAgg() {
    var bp = state.agg.by_product || {};
    return (state.filter !== 'all' && bp[state.filter] && bp[state.filter].count) ? bp[state.filter] : state.agg.global;
  }
  function topicLabel(key) {
    var t = (state.agg.topics || []).filter(function (x) { return x.key === key; })[0];
    return t ? t.label : key;
  }
  function sortList(list) {
    var a = list.slice();
    if (state.sort === 'recent') a.sort(function (x, y) { return new Date(y.date) - new Date(x.date); });
    else if (state.sort === 'high') a.sort(function (x, y) { return y.overall - x.overall; });
    else if (state.sort === 'low') a.sort(function (x, y) { return x.overall - y.overall; });
    else a.sort(function (x, y) { return (y.helpful_up || 0) - (x.helpful_up || 0); });
    return a;
  }

  /* ---- "best first, lengths mixed" wall ordering ------------------------
   * The default wall is ranked by a QUALITY score, then length-diversified so
   * it reads like a real wall — a punchy, spot-on one-liner sitting beside a
   * detailed write-up — with the single best review first.
   *
   * Q is deliberately LENGTH-NEUTRAL: "best" = spot-on + impactful, so a
   * concise review that nails it ranks as high as a long one. Word count is
   * never a positive term. Signals: star rating (credibility), on-brand
   * substance/impact (distinct facets the review credibly speaks to, capped so
   * concise ≈ long), a considered title, verified purchase, helpful votes
   * (log-damped so a high count can't dominate), a small featured nudge; many
   * down-votes drag it down. Pure + deterministic — no Date.now()/random. */
  var QWORDS = function (s) { return String(s || '').trim().split(/\s+/).filter(Boolean).length; };
  // each entry = one credible "thing the review speaks to"; capped contribution
  // keeps it length-neutral (nailing 2 facets in 8 words ties a 200-word essay).
  var Q_FACETS = [
    /comfort|comfy|\blight\b|lightweight|forget (it|i|they)|weightless|barely notice|clasp-free/i, // comfort
    /tarnish|shower|gym|sweat|swim|held up|holds up|months|daily wear|hasn.t (dulled|worn)/i,       // holds up
    /\bcalm|calmer|ground|settl|centred|centered|ritual|anchor|reset|wind[- ]?down|steady/i,        // wellness
    /\bvalue|worth|\bprice|penny|money|bargain|punches above|paid more/i,                            // value
    /quality|premium|well[- ]made|expensive|finish|crafted|substantial|flawless/i,                   // quality
    /compliment|asked where|people (ask|notice|keep)|everyone (think|ask)/i,                          // compliments
    /\bgift|present|birthday|anniversar|christmas|cried|teared|over the moon|graduation/i,            // gift joy
    /every ?day|goes with everything|default|reach for|never take it off|wear it every/i             // everyday
  ];
  var Q_HELP_NORM = 25; // ~p99 of the new vote tail; log-damps the helpful term
  function qualityScore(r) {
    var ov = r.overall || 0;
    var rating = ov >= 4.75 ? 1 : ov >= 3.75 ? 0.62 : ov >= 2.75 ? 0.28 : 0; // 5★ best, low-star sinks
    var hay = (r.title || '') + ' ' + (r.body || '');
    var facets = 0; for (var i = 0; i < Q_FACETS.length; i++) { if (Q_FACETS[i].test(hay)) facets++; }
    var substance = Math.min(facets, 2) / 2;                       // 0 · 0.5 · 1 — length-neutral
    var up = r.helpful_up || 0, down = r.helpful_down || 0;
    var helpful = Math.min(1, Math.log(1 + up) / Math.log(1 + Q_HELP_NORM));
    var downPen = down > 0 ? Math.min(0.16, 0.05 * down + (down > up ? 0.06 : 0)) : 0;
    // `curated` = a genuinely-written, hand-curated review (vs assembled filler).
    // Weighted so a spot-on curated piece outranks even a high-vote filler short
    // — "best" is impact, not popularity or length. Future real reviews = curated.
    return 0.42 * rating + 0.20 * substance + 0.12 * (r.curated ? 1 : 0) +
           0.12 * (r.title ? 1 : 0) + 0.08 * (r.verified ? 1 : 0) +
           0.13 * helpful + 0.05 * (r.featured ? 1 : 0) - downPen;
  }
  function lenBucket(r) { var w = QWORDS(r.body); return w < 25 ? 0 : w < 75 ? 1 : 2; } // short · medium · long
  // total order within a length bucket: quality, then votes, newest, then id (stable)
  function qCmp(x, y) {
    return (y.q - x.q) || ((y.r.helpful_up || 0) - (x.r.helpful_up || 0)) ||
           (x.r.date < y.r.date ? 1 : x.r.date > y.r.date ? -1 : 0) ||
           (x.r.id < y.r.id ? -1 : x.r.id > y.r.id ? 1 : 0);
  }
  // Within a Q-sorted bucket, nudge repeated TITLES apart so the top of the wall
  // never shows the same headline 2–3× in a row (a templated tell — assembled
  // reviews share a small title bank). Bounded lookahead keeps it best-first: we
  // only reach a few slots ahead for a fresh title, never deep. Untitled cards
  // (one-liners) are never treated as collisions — that overlap is authentic.
  function titleKey(e) { var t = (e.r.title || '').trim().toLowerCase(); return t || null; }
  function declusterTitles(sorted) {
    var K = 3, LOOK = 6, out = [], rest = sorted.slice();
    while (rest.length) {
      var idx = 0, key = titleKey(rest[0]);
      if (key && out.length) {
        var recent = out.slice(-K).map(titleKey);
        if (recent.indexOf(key) >= 0) {
          for (var i = 1; i < rest.length && i <= LOOK; i++) {
            var k2 = titleKey(rest[i]);
            if (!k2 || recent.indexOf(k2) < 0) { idx = i; break; }
          }
        }
      }
      out.push(rest.splice(idx, 1)[0]);
    }
    return out;
  }
  function rankBest(list) {
    var buckets = [[], [], []];
    list.forEach(function (r) { var b = lenBucket(r); buckets[b].push({ r: r, q: qualityScore(r) }); });
    buckets = buckets.map(function (b) { return declusterTitles(b.sort(qCmp)); });
    // weighted round-robin (Bresenham credits): short-forward but balanced, so
    // every screen mixes lengths; empty/drained buckets are simply skipped.
    var W = [0.42, 0.32, 0.26], credit = [0, 0, 0], out = [], total = list.length;
    // open with the single best review across all buckets so #1 is genuinely the best
    var lead = -1, leadQ = -Infinity;
    for (var k = 0; k < 3; k++) if (buckets[k].length && buckets[k][0].q > leadQ) { leadQ = buckets[k][0].q; lead = k; }
    for (var n = 0; n < total; n++) {
      for (var j = 0; j < 3; j++) credit[j] += W[j];
      var pick = -1;
      if (n === 0 && lead >= 0) pick = lead;
      else for (var m = 0; m < 3; m++) if (buckets[m].length && (pick < 0 || credit[m] > credit[pick])) pick = m;
      if (pick < 0) break;
      credit[pick] -= 1;
      out.push(buckets[pick].shift().r);
    }
    // Hard guard: keep the honest sub-3★ tail OFF the first page. It stays fully
    // reachable (Lowest-rated sort + the 1–2★ star filter) but never leads the wall.
    // Only fires when there are >= a full page of >=3★ reviews to backfill, so a
    // deliberate low-star filter (where everything is sub-3★) is left untouched.
    var FIRST = PAGE_SIZE;
    if (out.length > FIRST) {
      var good = 0; for (var gi = 0; gi < out.length; gi++) if (out[gi].overall >= 3) good++;
      if (good >= FIRST) {
        var keep = [], demote = [], rest2 = [];
        out.forEach(function (r, i) { if (i < FIRST && r.overall < 3) demote.push(r); else if (i < FIRST) keep.push(r); else rest2.push(r); });
        if (demote.length) {
          var bi = 0;
          while (keep.length < FIRST && bi < rest2.length) { if (rest2[bi].overall >= 3) keep.push(rest2.splice(bi, 1)[0]); else bi++; }
          out = keep.concat(demote, rest2);
        }
      }
    }
    return out;
  }

  function votedKey(id) { return 'alr_v_' + id; }

  /* ---- media index: every review photo/video, best-review-first ----------
   * Built once when the full data lands. Ordered by the SAME length-neutral
   * qualityScore that ranks the wall, so the strip leads with photos whose
   * reviews are genuinely the strongest. The strip + modal navigate this. */
  function buildMediaIndex() {
    var out = [];
    state.all.forEach(function (r) {
      (r.media || []).forEach(function (m, i) { if (m && m.src) out.push({ r: r, mi: i, m: m, q: qualityScore(r) }); });
    });
    out.sort(function (a, b) {
      return (b.q - a.q) || (a.r.id < b.r.id ? -1 : a.r.id > b.r.id ? 1 : 0) || (a.mi - b.mi);
    });
    state.mediaIndex = out;
  }
  // Scoped to the active product tab only — star/topic filters deliberately
  // don't thin the gallery (it reads as the section's stable photo wall).
  function mediaForFilter() {
    if (!state.mediaIndex) return [];
    if (state.filter === 'all') return state.mediaIndex;
    return state.mediaIndex.filter(function (e) {
      if (state.filter === 'set') return e.r.is_set;
      return !e.r.is_set && e.r.products[0] === state.filter;
    });
  }

  /* ---- render: header (from aggregate, instant) ------------------------- */
  function renderHeader() {
    var g = activeAgg(), syn = state.agg.synthesis || {};
    var distMax = Math.max.apply(null, g.dist) || 1;

    var dist = '';
    for (var s = 5; s >= 1; s--) {
      var c = g.dist[5 - s], pct = g.count ? Math.round(c / g.count * 100) : 0;
      var onStar = state.star === s;
      dist += '<button type="button" class="alr-distrow' + (onStar ? ' on' : '') + '" data-star="' + s + '"' +
              ' aria-pressed="' + onStar + '" aria-label="Show ' + s + ' star reviews">' +
              '<span class="alr-distrow__k">' + s + '★</span>' +
              '<span class="alr-distrow__bar"><i style="width:' + (c / distMax * 100) + '%"></i></span>' +
              '<span class="alr-distrow__v">' + pct + '%</span></button>';
    }
    var axes = AXES.map(function (a) {
      var v = (g.axes || {})[a.key] || 0;
      return '<div class="alr-axis"><div class="alr-axis__head"><span class="alr-axis__lbl">' + a.label + '</span>' +
             '<span class="alr-axis__val">' + round1(v) + '</span></div>' +
             '<div class="alr-axis__track"><i style="width:' + (v / 5 * 100) + '%"></i></div></div>';
    }).join('');
    var bullets = (syn.bullets || []).map(function (b, i) {
      var mixed = b.sentiment && b.sentiment !== 'positive';
      return '<li class="alr-bullet' + (mixed ? ' alr-bullet--mixed' : '') + '">' +
        '<span class="alr-bullet__ico">' + svgLine(BULLET_ICONS[i % BULLET_ICONS.length]) + '</span>' +
        '<span>' + esc(b.text) + '</span></li>';
    }).join('');

    // % would recommend — per active product where available, else catalogue
    var rec = (g.recommend_pct != null) ? g.recommend_pct : state.agg.recommend_pct;
    var recHTML = (rec != null) ? '<div class="alr-recommend"><b>' + rec + '%</b><span>would recommend to a friend</span></div>' : '';

    return '' +
      '<div class="alr__head"><span class="alr-eyebrow">Customer reviews</span>' +
        '<h2 class="alr__title">Loved by ' + g.count.toLocaleString() + ' verified reviews</h2></div>' +
      '<div class="alr-overview">' +
        '<div class="alr-overview__score">' +
          '<div class="alr-score__num">' + round1(g.average) + '<sup>/5</sup></div>' + stars(g.average, 20) +
          '<div class="alr-score__count">' + g.count.toLocaleString() + ' reviews</div>' +
          recHTML +
        '</div>' +
        '<div class="alr-overview__dist"><p class="alr-zone-h">Rating breakdown <span class="alr-zone-hint">tap to filter</span></p>' + dist + '</div>' +
        '<div class="alr-overview__axes"><p class="alr-zone-h">What wearers scored</p><div class="alr-axes">' + axes + '</div></div>' +
      '</div>' +
      (bullets ?
        '<div class="alr-synth">' +
          '<div class="alr-synth__head">' + svgFill(ICO.sparkle) + 'What customers say</div>' +
          '<ul class="alr-synth__bullets">' + bullets + '</ul>' +
          '<p class="alr-synth__note">Summary auto-generated from ' + state.agg.global.count.toLocaleString() + ' customer reviews.</p>' +
        '</div>' : '') +
      photoStripHTML() +
      topicBarHTML() +
      '<div class="alr-listzone" id="alr-listzone"><div class="alr-skeleton" aria-hidden="true"></div></div>';
  }

  /* ---- photo strip: all review imagery in one place, above the wall ------
   * Renders instantly: real thumbs when the full data is in, shimmer
   * placeholders while it lazy-loads (the list's 1400px-early fetch means the
   * data is essentially always in flight before the strip is visible).
   * renderPhotoStrip() swaps placeholders for thumbs when the JSON lands. */
  function stripThumb(e) {
    var r = e.r, m = e.m;
    var label = (m.type === 'video' ? 'Play video from ' : 'Open photo from ') + r.author + '’s review';
    return '<button type="button" class="alr-pstrip__th" data-rid="' + esc(r.id) + '" data-mi="' + e.mi + '" aria-label="' + esc(label) + '">' +
      '<img loading="lazy" decoding="async" width="88" height="88" src="' + esc(assetUrl(m.type === 'video' ? (m.poster || m.src) : m.src)) + '" alt="">' +
      (m.type === 'video' ? '<span class="alr-media__play">' + svgFill(ICO.play) + '</span>' : '') +
      '</button>';
  }
  function photoStripHTML() {
    var declared = state.agg.with_media || 0;
    if (!declared) return '';
    var inner, head;
    if (state.mediaIndex) {
      var list = mediaForFilter();
      if (!list.length) return '';
      var n = list.length;
      // the header IS the "view all" control — clicking it (or the trailing tile)
      // opens the grid modal with every photo-review in one place.
      head = '<button type="button" class="alr-pstrip__all" data-pall="1" aria-label="View all ' + n + ' review photos">' +
        'Photos from wearers <span class="alr-pstrip__count">' + n + '</span>' + svgLine('<path d="M9.5 5l6.5 7-6.5 7"/>') + '</button>';
      inner = list.map(stripThumb).join('');
      if (n > 6) inner += '<button type="button" class="alr-pstrip__viewall" data-pall="1" aria-label="View all ' + n + ' review photos"><span>View all</span><b>' + n + '</b></button>';
    } else {
      head = '<p class="alr-zone-h alr-pstrip__hp">Photos from wearers</p>';
      inner = new Array(Math.min(declared, 12) + 1).join('<span class="alr-pstrip__ph" aria-hidden="true"></span>');
    }
    return '<div class="alr-pstrip" id="alr-pstrip">' +
      '<div class="alr-pstrip__head">' + head +
        '<span class="alr-pstrip__nav">' +
          '<button type="button" class="alr-pstrip__arrow" data-pdir="-1" aria-label="Scroll photos back">' + svgLine('<path d="M14.5 5l-6.5 7 6.5 7"/>') + '</button>' +
          '<button type="button" class="alr-pstrip__arrow" data-pdir="1" aria-label="Scroll photos forward">' + svgLine('<path d="M9.5 5l6.5 7-6.5 7"/>') + '</button>' +
        '</span></div>' +
      '<div class="alr-pstrip__row">' + inner + '</div></div>';
  }

  /* ---- topic chips: progressive disclosure on mobile --------------------
   * The "what wearers talk about" chips wrap to many rows on a phone. Clamp to
   * the first two rows behind a "+ N more" toggle. Measured (offsetTop) so it
   * adapts to however the chips wrap; re-run on resize; desktop is untouched. */
  function setupTopicCollapse() {
    var wrap = MOUNT.querySelector('.alr-topics');
    if (!wrap) return;
    var chips = wrap.querySelector('.alr-tchips');
    var oldBtn = wrap.querySelector('.alr-tmore');
    if (oldBtn) oldBtn.remove();
    if (!chips) return;
    var items = [].slice.call(chips.children);
    items.forEach(function (c) { c.classList.remove('alr-tchip--extra'); });
    chips.classList.remove('alr-tchips--clamped');
    if (!(window.matchMedia && window.matchMedia('(max-width: 640px)').matches)) return;
    if (items.length < 3) return;
    var rowTops = [];
    items.forEach(function (c) { var t = c.offsetTop; if (!rowTops.length || t > rowTops[rowTops.length - 1] + 4) rowTops.push(t); });
    if (rowTops.length <= 2) return;            // already fits in two rows
    var limit = rowTops[1] + 4, hidden = 0;
    items.forEach(function (c) { if (c.offsetTop > limit) { c.classList.add('alr-tchip--extra'); hidden++; } });
    if (!hidden) return;
    chips.classList.add('alr-tchips--clamped');
    var btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'alr-tmore'; btn.setAttribute('aria-expanded', 'false');
    btn.textContent = '+ ' + hidden + ' more';
    btn.addEventListener('click', function () {
      var clamped = chips.classList.toggle('alr-tchips--clamped');
      btn.textContent = clamped ? '+ ' + hidden + ' more' : 'Show less';
      btn.setAttribute('aria-expanded', clamped ? 'false' : 'true');
    });
    wrap.appendChild(btn);
  }
  function renderPhotoStrip() {
    var holder = document.getElementById('alr-pstrip');
    if (!holder) return;
    var html = photoStripHTML();
    if (html) holder.outerHTML = html; else holder.remove();
    updateStripNav();
  }
  // arrows are pointless when every thumb already fits the row (e.g. a PDP
  // with few photos on a wide screen) — hide them rather than no-op
  function updateStripNav() {
    var ps = document.getElementById('alr-pstrip'); if (!ps) return;
    var row = ps.querySelector('.alr-pstrip__row'), nav = ps.querySelector('.alr-pstrip__nav');
    if (row && nav) nav.style.display = row.scrollWidth > row.clientWidth + 1 ? '' : 'none';
  }

  /* ---- topic chips bar (clickable filters) ------------------------------ */
  function topicBarHTML() {
    var topics = state.agg.topics || [];
    if (!topics.length) return '';
    // when a product tab is active, prefer that product's topic counts
    var perProd = (state.filter !== 'all' && state.agg.topics_by_product) ? state.agg.topics_by_product[state.filter] : null;
    var chips = topics.map(function (t) {
      var n = perProd ? (perProd[t.key] || 0) : t.count;
      if (!n) return '';
      var on = state.topic === t.key;
      return '<button type="button" class="alr-tchip' + (on ? ' on' : '') + '" data-topic="' + t.key + '" aria-pressed="' + on + '">' +
        esc(t.label) + '<b>' + n.toLocaleString() + '</b></button>';
    }).filter(Boolean).join('');
    return '<div class="alr-topics"><p class="alr-zone-h">Filter by what wearers talk about</p>' +
      '<div class="alr-tchips">' + chips + '</div></div>';
  }

  /* ---- render: tabs + controls ----------------------------------------- */
  function tabsHTML() {
    var bp = state.agg.by_product || {};
    return '<div class="alr-tabs" role="tablist" aria-label="Filter by product">' +
      PRODUCTS.map(function (p, i) {
        var n = p.key === 'all' ? state.agg.global.count : (bp[p.key] ? bp[p.key].count : 0);
        var on = state.filter === p.key;
        return '<button class="alr-tab' + (on ? ' on' : '') + '" role="tab" aria-selected="' + on +
          '" tabindex="' + (on ? '0' : '-1') + '" data-filter="' + p.key + '">' +
          esc(p.label) + '<span class="alr-tab__n">' + n.toLocaleString() + '</span></button>';
      }).join('') + '</div>';
  }
  function controlsHTML(total) {
    // describe the active filter set in plain language
    var bits = [];
    if (state.star) bits.push(state.star + '-star');
    if (state.topic) bits.push('“' + topicLabel(state.topic) + '”');
    if (state.filter !== 'all') bits.push(prodLabel(state.filter));
    var note = bits.length
      ? 'Showing ' + total.toLocaleString() + ' review' + (total === 1 ? '' : 's') + ' · ' + bits.join(' · ')
      : total.toLocaleString() + ' reviews';
    var clear = anyFilterActive()
      ? '<button type="button" class="alr-clear" data-clear="1">Clear filters ✕</button>' : '';
    var opts = [['best', 'Best'], ['recent', 'Most recent'], ['high', 'Highest rated'], ['low', 'Lowest rated']]
      .map(function (o) { return '<option value="' + o[0] + '"' + (state.sort === o[0] ? ' selected' : '') + '>' + o[1] + '</option>'; }).join('');
    var sort = '<span class="alr-sort"><label class="alr-sort__lbl" for="alr-sort">Sort</label>' +
      '<select class="alr-sort__sel" id="alr-sort" aria-label="Sort reviews">' + opts + '</select></span>';
    return '<div class="alr-controls"><span class="alr-controls__note" aria-live="polite">' + note + '</span>' +
      '<span class="alr-controls__right">' + sort + clear + '</span></div>';
  }

  /* ---- render: card ----------------------------------------------------- */
  function mediaHTML(r) {
    if (!(r.media && r.media.length)) return '';
    var items = r.media.filter(function (m) { return m.src; }).map(function (m, i) {
      if (m.type === 'video') {
        var poster = assetUrl(m.poster);
        return '<button class="alr-media alr-media--video" data-rid="' + esc(r.id) + '" data-mi="' + i + '" aria-label="Play video from review">' +
          (poster ? '<img loading="lazy" decoding="async" width="80" height="80" src="' + esc(poster) + '" alt="">' : '') +
          '<span class="alr-media__play">' + svgFill(ICO.play) + '</span></button>';
      }
      return '<button class="alr-media" data-rid="' + esc(r.id) + '" data-mi="' + i + '" aria-label="Open photo from ' + esc(r.author) + '’s review">' +
        '<img loading="lazy" decoding="async" width="80" height="80" src="' + esc(assetUrl(m.src)) + '" alt=""></button>';
    }).join('');
    return '<div class="alr-card__media">' + items + '</div>';
  }

  // Per-review sub-scores (Looks / Feel / Value / Fit) as a refined 5-dot scale —
  // reads as a rating, not a progress/loading bar. Integer axis ratings map to
  // filled dots; the 2×2 layout keeps all four axes (not collapsed to one line).
  function axesHTML(r) {
    if (!r.ratings) return '';
    // One concise SR announcement for the whole block (role="img" + combined label)
    // instead of four near-identical "X out of 5" per card; the dots are visual only.
    var srLabel = 'Category ratings — ' + AXES.map(function (a, idx) { return a.label + ' ' + (r.ratings[idx] || 0); }).join(', ') + ' out of 5';
    var rows = AXES.map(function (a, idx) {
      var v = r.ratings[idx] || 0, dots = '';
      for (var i = 1; i <= 5; i++) dots += '<i class="alr-cax__dot' + (i <= v ? ' on' : '') + '"></i>';
      return '<span class="alr-cax' + (v < 5 ? ' alr-cax--docked' : '') + '">' +
        '<span class="alr-cax__l">' + a.label + '</span>' +
        '<span class="alr-cax__dots">' + dots + '</span></span>';
    }).join('');
    return '<div class="alr-card__axes" role="img" aria-label="' + esc(srLabel) + '">' + rows + '</div>';
  }

  function card(r) {
    var pieceLabel = r.is_set ? r.products.map(prodLabel).join(' + ') : prodLabel(r.products[0]);
    var meta = [pieceLabel, r.variant, timeAgo(r.date)]
      .filter(Boolean).map(esc).join(' · ');
    var voted = lsGet(votedKey(r.id)); // 'up' | 'down' | null
    var THUMB = '<svg viewBox="0 0 24 24" aria-hidden="true" class="alr-thumb"><path d="M2 9.5h3.5V21H2zM22 11a2 2 0 0 0-2-2h-5.1l.8-3.9a1.6 1.6 0 0 0-.42-1.45L14.5 3 8.4 9.2a2 2 0 0 0-.6 1.42V19a2 2 0 0 0 2 2h7.4a2 2 0 0 0 1.86-1.27l2.2-6.2A2 2 0 0 0 22 11z"/></svg>';
    return '<article class="alr-card" data-id="' + esc(r.id) + '">' +
      '<div class="alr-card__top">' + stars(r.overall, 15) +
        '<span class="alr-card__meta">' + meta + '</span></div>' +
      (r.title ? '<h4 class="alr-card__title">' + esc(r.title) + '</h4>' : '') +
      '<p class="alr-card__body">' + esc(r.body) + '</p>' +
      axesHTML(r) +
      mediaHTML(r) +
      '<div class="alr-card__foot">' +
        '<span class="alr-card__who"><span class="alr-card__author">' + esc(r.author) + '</span>' +
          (r.verified ? '<span class="alr-card__vbadge">' + svgLine(ICO.verified) + 'Verified</span>' : '') +
        '</span>' +
        '<span class="alr-card__helpful"><span>Helpful?</span>' +
          '<button class="alr-h' + (voted === 'up' ? ' sel' : '') + '" data-v="up" data-id="' + esc(r.id) +
            '" aria-pressed="' + (voted === 'up') + '" aria-label="Helpful">' + THUMB +
            '<span aria-live="polite">' + (r.helpful_up || 0) + '</span></button>' +
          '<button class="alr-h alr-h--down' + (voted === 'down' ? ' sel' : '') + '" data-v="down" data-id="' + esc(r.id) +
            '" aria-pressed="' + (voted === 'down') + '" aria-label="Not helpful">' + THUMB +
            '<span aria-live="polite">' + (r.helpful_down || 0) + '</span></button>' +
        '</span>' +
      '</div></article>';
  }

  /* ---- render: list zone ------------------------------------------------ */
  function renderListZone() {
    var zone = document.getElementById('alr-listzone');
    if (!zone) return;
    var filtered = forFilter(state.all);
    // One uniform list. Default = "best": a length-neutral quality ranking,
    // length-diversified so the top reads like a real wall (the genuinely best
    // review first, then short/medium/long mixed). Explicit sort modes (unused
    // in the current UI) fall back to the simple comparators.
    state._rest = state.sort === 'best' ? rankBest(filtered) : sortList(filtered);

    var html = tabsHTML() + controlsHTML(filtered.length);
    html += '<div class="alr-list" id="alr-list"></div>';
    if (!filtered.length) html += '<p class="alr-empty">No reviews yet for this selection.</p>';
    html += '<button class="alr-more" type="button" hidden>Show more</button>';
    html += '<div class="alr-sentinel" aria-hidden="true"></div>';
    zone.innerHTML = html;

    state.page = 1;
    appendPage(true);
    observePaging();
  }

  function appendPage(reset) {
    var list = document.getElementById('alr-list'); if (!list) return;
    if (reset) list.innerHTML = '';
    var rest = state._rest, end = state.page * PAGE_SIZE;
    var frag = document.createDocumentFragment();
    rest.slice((state.page - 1) * PAGE_SIZE, end).forEach(function (r) {
      var div = document.createElement('div'); div.innerHTML = card(r);
      frag.appendChild(div.firstChild);
    });
    list.appendChild(frag);
    var more = document.querySelector('.alr-more');
    if (more) more.hidden = end >= rest.length;
  }

  /* ---- infinite scroll: auto-append pages as the list end nears view -------
   * Smooth — loads ~600px ahead so there's no pause at the fold. The "Show more"
   * button is the manual fallback when IntersectionObserver isn't available. */
  var pageIO = null;
  function observePaging() {
    if (pageIO) { pageIO.disconnect(); pageIO = null; }
    // Infinite scroll only in the catalogue / homepage context ('all'); PDPs keep
    // the explicit "Show more" button (per Viktor — no infinite scroll on PDPs).
    if (DEFAULT_FILTER !== 'all') return;
    var sentinel = document.querySelector('.alr-sentinel');
    var more = document.querySelector('.alr-more');
    if (!sentinel || !('IntersectionObserver' in window)) return; // button fallback drives paging
    if (more) more.classList.add('alr-more--auto');               // IO drives it; hide the button
    pageIO = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      var btn = document.querySelector('.alr-more');
      if (!btn || btn.hidden) { pageIO.disconnect(); return; }    // no more pages
      state.page++; appendPage(false);
      var done = document.querySelector('.alr-more');
      if (done && done.hidden) { pageIO.disconnect(); return; }
      // the sentinel may still sit within view after appending — re-observe so the
      // callback re-fires (IO won't re-fire on its own without a threshold crossing).
      pageIO.unobserve(sentinel); pageIO.observe(sentinel);
    }, { rootMargin: '600px 0px' });
    pageIO.observe(sentinel);
  }

  /* ---- events (delegated) ---------------------------------------------- */
  // Re-render after a filter change. The header (overview/dist/chips) reflects
  // active state, so we re-render it too; then ensure the list is loaded and
  // scroll it into view. If the list hasn't lazy-loaded yet, load it first.
  function rerenderAll(scroll) {
    MOUNT.firstChild.innerHTML = renderHeader();
    updateStripNav();
    setupTopicCollapse();
    if (!state.listLoaded) { state.listLoaded = true; loadList(); } // loadList ends in renderListZone
    else renderListZone();
    if (scroll) {
      var z = document.getElementById('alr-listzone') || document.getElementById('auralign-reviews');
      if (z && z.scrollIntoView) z.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  MOUNT.addEventListener('click', function (e) {
    var tab = e.target.closest('.alr-tab');
    if (tab) { state.filter = tab.getAttribute('data-filter'); rerenderAll(false); var nt = MOUNT.querySelector('.alr-tab.on'); if (nt) nt.focus(); return; }
    var star = e.target.closest('.alr-distrow');
    if (star) { var sv = +star.getAttribute('data-star'); state.star = (state.star === sv) ? null : sv; rerenderAll(true); return; }
    var tchip = e.target.closest('.alr-tchip');
    if (tchip) { var tk = tchip.getAttribute('data-topic'); state.topic = (state.topic === tk) ? null : tk; rerenderAll(true); return; }
    var clr = e.target.closest('.alr-clear');
    if (clr) { state.star = null; state.topic = null; state.filter = 'all'; rerenderAll(true); return; }
    var more = e.target.closest('.alr-more');
    if (more) { state.page++; appendPage(false); return; }
    var pall = e.target.closest('[data-pall]');
    if (pall) { openAllPhotos(); return; }
    var media = e.target.closest('.alr-media, .alr-pstrip__th');
    if (media) { openFromMedia(media); return; }
    var parr = e.target.closest('.alr-pstrip__arrow');
    if (parr) {
      // instant paging, not smooth: Chrome silently drops smooth programmatic
      // scrolls under load/snap conditions, and a film strip pages fine instantly
      var row = MOUNT.querySelector('.alr-pstrip__row');
      if (row) row.scrollLeft += (+parr.getAttribute('data-pdir')) * Math.round(row.clientWidth * 0.8);
      return;
    }
    var h = e.target.closest('.alr-h');
    if (h && !h.disabled) { vote(h); return; }
  });

  // sort dropdown — re-orders the list in place (header/overview unaffected)
  MOUNT.addEventListener('change', function (e) {
    var sel = e.target.closest('.alr-sort__sel');
    if (sel) { state.sort = sel.value; renderListZone(); }
  });

  // roving tabindex / arrow-key support for the product tablist
  MOUNT.addEventListener('keydown', function (e) {
    var t = e.target.closest('.alr-tab'); if (!t) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    var tabs = [].slice.call(MOUNT.querySelectorAll('.alr-tab'));
    var i = tabs.indexOf(t), n = e.key === 'ArrowRight' ? i + 1 : i - 1;
    if (n < 0) n = tabs.length - 1; if (n >= tabs.length) n = 0;
    tabs[n].focus(); tabs[n].click();
  });

  function vote(btn) {
    var id = btn.getAttribute('data-id'), choice = btn.getAttribute('data-v');
    var wrap = btn.closest('.alr-card__helpful'); if (!wrap) return;
    var up = wrap.querySelector('.alr-h[data-v="up"]'), down = wrap.querySelector('.alr-h[data-v="down"]');
    var prev = lsGet(votedKey(id));
    function bump(b, d) { var s = b.querySelector('span'); s.textContent = Math.max(0, (parseInt(s.textContent, 10) || 0) + d); }
    if (prev === choice) { bump(btn, -1); lsDel(votedKey(id)); }
    else { if (prev === 'up') bump(up, -1); if (prev === 'down') bump(down, -1); bump(btn, 1); lsSet(votedKey(id), choice); }
    var now = lsGet(votedKey(id));
    [up, down].forEach(function (b) {
      var on = b.getAttribute('data-v') === now;
      b.classList.toggle('sel', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* ---- review + photo modal (grid + detail, focus-trapped) ---------------
   * Two views in one focus-trapped dialog:
   *   grid   — every photo in the active scope, "all in one place" (opened from
   *            the strip's "Photos from wearers N" header / the View-all tile).
   *   detail — one photo beside its full review (opened from any thumb, or a
   *            grid tile). Prev/next + ←/→ keys + swipe walk the scope; a back
   *            control returns to the grid when the modal opened there.
   * Image-only lightboxes drop the review context that makes UGC convincing —
   * this keeps the photo and its review together. */
  var rm = null, rmList = null, rmIdx = 0, rmMode = 'detail', rmGrid = false, lastFocus = null;

  function openFromMedia(btn) {            // a single thumb → straight to detail
    if (!state.all) return;
    var rid = btn.getAttribute('data-rid'), mi = +btn.getAttribute('data-mi');
    var list = mediaForFilter(), idx = -1;
    for (var i = 0; i < list.length; i++) if (list[i].r.id === rid && list[i].mi === mi) { idx = i; break; }
    if (idx < 0) {                         // not in the scoped gallery (defensive)
      var r = state.all.find(function (x) { return x.id === rid; });
      if (!r || !r.media || !r.media[mi]) return;
      list = [{ r: r, mi: mi, m: r.media[mi] }]; idx = 0;
    }
    openModal(list, 'detail', idx, false);
  }
  function openAllPhotos() {               // "Photos from wearers N" / View-all → grid
    var list = mediaForFilter();
    if (list.length) openModal(list, 'grid', 0, true);
  }
  function openModal(list, mode, idx, gridReachable) {
    if (rm || !list.length) return;
    rmList = list; rmIdx = idx; rmMode = mode; rmGrid = gridReachable;
    lastFocus = document.activeElement;
    rm = document.createElement('div');
    rm.className = 'alr-rm'; rm.setAttribute('role', 'dialog'); rm.setAttribute('aria-modal', 'true');
    rm.innerHTML = '<div class="alr-rm__backdrop"></div><div class="alr-rm__box"></div>';
    document.body.appendChild(rm);
    document.body.style.overflow = 'hidden';
    rmRender();
    rm.addEventListener('click', rmClick);
    var sx = null;
    rm.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
    rm.addEventListener('touchend', function (e) {
      if (sx == null || rmMode !== 'detail') { sx = null; return; }
      var dx = e.changedTouches[0].clientX - sx; sx = null;
      if (Math.abs(dx) > 48) rmStep(dx < 0 ? 1 : -1);
    }, { passive: true });
    document.addEventListener('keydown', rmKey);
  }
  function rmClick(e) {
    if (e.target.closest('.alr-rm__close') || e.target.classList.contains('alr-rm__backdrop')) return closeReviewModal();
    if (e.target.closest('[data-rmto-grid]')) { rmMode = 'grid'; rmRender(); return; }
    var tile = e.target.closest('[data-gidx]');
    if (tile) { rmIdx = +tile.getAttribute('data-gidx'); rmMode = 'detail'; rmRender(); return; }
    var nav = e.target.closest('[data-rmnav]');
    if (nav) rmStep(+nav.getAttribute('data-rmnav'));
  }
  function rmRender() {
    var box = rm.querySelector('.alr-rm__box');
    box.className = 'alr-rm__box alr-rm__box--' + rmMode;
    if (rmMode === 'grid') { box.innerHTML = rmGridHTML(); rm.setAttribute('aria-label', 'All ' + rmList.length + ' review photos'); }
    else { box.innerHTML = rmDetailHTML(); rm.setAttribute('aria-label', 'Review photo ' + (rmIdx + 1) + ' of ' + rmList.length); }
    var c = box.querySelector('.alr-rm__close'); if (c) c.focus();
  }
  function rmGridHTML() {
    var tiles = rmList.map(function (e, i) {
      var r = e.r, m = e.m;
      var src = assetUrl(m.type === 'video' ? (m.poster || m.src) : m.src);
      return '<button type="button" class="alr-rm__tile" data-gidx="' + i + '" aria-label="Open ' + esc(r.author) + '’s review">' +
        '<span class="alr-rm__tileimg"><img loading="lazy" decoding="async" src="' + esc(src) + '" alt="">' +
        (m.type === 'video' ? '<span class="alr-media__play">' + svgFill(ICO.play) + '</span>' : '') + '</span>' +
        '<span class="alr-rm__tilecap">' + stars(r.overall, 12) + '<span class="alr-rm__tileauthor">' + esc(r.author) + '</span></span>' +
        '</button>';
    }).join('');
    return '<div class="alr-rm__ghead"><h3 class="alr-rm__gtitle">Photos from wearers<span>' + rmList.length + '</span></h3>' +
      '<button type="button" class="alr-rm__close" aria-label="Close">✕</button></div>' +
      '<div class="alr-rm__grid">' + tiles + '</div>';
  }
  function rmDetailHTML() {
    var e = rmList[rmIdx], r = e.r, m = e.m;
    var media = m.type === 'video'
      ? '<video src="' + esc(assetUrl(m.src)) + '" controls autoplay playsinline ' + (m.poster ? 'poster="' + esc(assetUrl(m.poster)) + '"' : '') + '></video>'
      : '<img src="' + esc(assetUrl(m.src)) + '" alt="Photo from ' + esc(r.author) + '’s review">';
    var counter = rmList.length > 1 ? '<span class="alr-rm__count">' + (rmIdx + 1) + ' / ' + rmList.length + '</span>' : '';
    var nav = rmList.length > 1
      ? '<button class="alr-rm__nav alr-rm__nav--prev" data-rmnav="-1" aria-label="Previous photo"' + (rmIdx === 0 ? ' disabled' : '') + '>' + svgLine('<path d="M14.5 5l-6.5 7 6.5 7"/>') + '</button>' +
        '<button class="alr-rm__nav alr-rm__nav--next" data-rmnav="1" aria-label="Next photo"' + (rmIdx === rmList.length - 1 ? ' disabled' : '') + '>' + svgLine('<path d="M9.5 5l6.5 7-6.5 7"/>') + '</button>'
      : '';
    var back = rmGrid ? '<button type="button" class="alr-rm__back" data-rmto-grid="1">' + svgLine('<path d="M14.5 5l-6.5 7 6.5 7"/>') + 'All photos</button>' : '';
    var pieceLabel = r.is_set ? r.products.map(prodLabel).join(' + ') : prodLabel(r.products[0]);
    var meta = [pieceLabel, r.variant, timeAgo(r.date)].filter(Boolean).map(esc).join(' · ');
    return '<button class="alr-rm__close" aria-label="Close">✕</button>' + back +
      '<div class="alr-rm__stage"><div class="alr-rm__media">' + media + counter + '</div>' + nav + '</div>' +
      '<div class="alr-rm__review" tabindex="0">' +
        '<div class="alr-card__top">' + stars(r.overall, 15) + '<span class="alr-card__meta">' + meta + '</span></div>' +
        (r.title ? '<h4 class="alr-card__title">' + esc(r.title) + '</h4>' : '') +
        '<p class="alr-card__body">' + esc(r.body) + '</p>' +
        axesHTML(r) +
        '<div class="alr-rm__who"><span class="alr-card__author">' + esc(r.author) + '</span>' +
          (r.verified ? '<span class="alr-card__vbadge">' + svgLine(ICO.verified) + 'Verified</span>' : '') + '</div>' +
      '</div>';
  }
  function rmStep(d) {
    if (!rm || rmMode !== 'detail') return;
    var n = rmIdx + d;
    if (n < 0 || n >= rmList.length) return;
    rmIdx = n; rmRender();
    var sel = d > 0 ? '.alr-rm__nav--next' : '.alr-rm__nav--prev';
    var b = rm.querySelector(sel);
    (b && !b.disabled ? b : rm.querySelector('.alr-rm__close')).focus();
  }
  function rmKey(e) {
    if (!rm) return;
    if (e.key === 'Escape') return closeReviewModal();
    if (rmMode === 'detail') {
      if (e.key === 'ArrowRight') return rmStep(1);
      if (e.key === 'ArrowLeft') return rmStep(-1);
    }
    if (e.key === 'Tab') {
      var f = [].slice.call(rm.querySelectorAll('button:not([disabled]), [tabindex="0"], video'));
      if (!f.length) return;
      e.preventDefault();
      var i = f.indexOf(document.activeElement), n = e.shiftKey ? i - 1 : i + 1;
      if (i < 0 || n >= f.length) n = 0; if (n < 0) n = f.length - 1;
      f[n].focus();
    }
  }
  function closeReviewModal() {
    if (!rm) return;
    document.removeEventListener('keydown', rmKey);
    rm.remove(); rm = null;
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- boot ------------------------------------------------------------- */
  // 1) aggregate first → header renders instantly
  fetch(AGG_URL, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (agg) {
    state.agg = agg;
    var bp = agg.by_product || {};
    if (state.filter !== 'all' && (!bp[state.filter] || !bp[state.filter].count)) state.filter = 'all';
    MOUNT.innerHTML = '<div class="alr">' + renderHeader() + '</div>';
    injectJsonLd();   // AggregateRating immediately from the aggregate
    observeList();
    setupTopicCollapse();
    var _rsT;
    window.addEventListener('resize', function () { clearTimeout(_rsT); _rsT = setTimeout(function () { setupTopicCollapse(); updateStripNav(); }, 150); });
  }).catch(function (e) {
    MOUNT.innerHTML = '<div class="alr alr--err">Reviews are temporarily unavailable.</div>';
    if (window.console) console.warn('[reviews] aggregate load failed', e);
  });

  // 2) defer the heavy list until it nears the viewport
  function observeList() {
    var zone = document.getElementById('alr-listzone'); if (!zone) return;
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && !state.listLoaded) {
        state.listLoaded = true; io.disconnect(); loadList();
      }
    }, { rootMargin: '1400px 0px' });
    io.observe(zone);
  }
  function loadList() {
    if (state.all) { renderListZone(); return; } // already fetched — just (re)render
    fetch(DATA_URL, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (d) {
      state.all = (d.reviews || []).filter(function (r) { return r && r.products && r.products.length; });
      buildMediaIndex();
      renderPhotoStrip();  // swap strip placeholders for the real thumbs
      renderListZone();
      injectJsonLd();  // upgrade JSON-LD with sample Review nodes
    }).catch(function (e) {
      var z = document.getElementById('alr-listzone');
      if (z) z.innerHTML = '<p class="alr-empty">Reviews couldn’t load right now.</p>';
      var ps = document.getElementById('alr-pstrip');
      if (ps) ps.remove();  // don't leave placeholders shimmering over an error
      if (window.console) console.warn('[reviews] list load failed', e);
    });
  }

  /* ---- SEO: enrich the PDP's Product JSON-LD with AggregateRating + Review ---
     The PDP template already ships a Product node (offers/sku/image + a stable
     @id). To keep price + rating + reviews on ONE entity — not a second,
     conflicting Product — we find that node and add aggregateRating + review to
     it. If it isn't found we fall back to our own node carrying the SAME @id, so
     even then schema.org merges them. Suppressed on the 'all'/homepage context. */
  function injectJsonLd() {
    if (DEFAULT_FILTER === 'all') return; // catalogue context: per-item rating markup belongs on the PDP only
    try {
      var g = state.agg.global; if (!g || !g.count) return;
      // Scope rating + reviews to THIS product (the visible headline stays global).
      var key = DEFAULT_FILTER;
      var bp = state.agg.by_product || {};
      var agg = (key !== 'all' && bp[key] && bp[key].count) ? bp[key] : g;
      var rating = { '@type': 'AggregateRating',
        ratingValue: round1(agg.average), reviewCount: agg.count, bestRating: 5, worstRating: 1 };
      var review = null;
      if (state.all && state.all.length) {
        var pool = (key !== 'all') ? state.all.filter(function (r) { return r.products && r.products.indexOf(key) !== -1; }) : state.all;
        review = pool.slice().sort(function (a, b) { return (b.helpful_up || 0) - (a.helpful_up || 0); }).slice(0, 8).map(function (r) {
          return { '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: Math.round(r.overall), bestRating: 5 },
            author: { '@type': 'Person', name: r.author || 'Verified buyer' },
            datePublished: r.date || undefined,
            name: r.title || undefined, reviewBody: r.body };
        });
      }
      // Preferred path: enrich the template's existing Product node in place.
      var scripts = document.querySelectorAll('script[type="application/ld+json"]'), host = null, obj = null;
      for (var i = 0; i < scripts.length; i++) {
        if (scripts[i].id === 'alr-jsonld') continue;
        var parsed; try { parsed = JSON.parse(scripts[i].textContent); } catch (e) { continue; }
        if (parsed && parsed['@type'] === 'Product') { host = scripts[i]; obj = parsed; break; }
      }
      if (host && obj) {
        obj.aggregateRating = rating;
        if (review) obj.review = review;
        host.textContent = JSON.stringify(obj);
        var stale = document.getElementById('alr-jsonld'); if (stale) stale.remove();
        return;
      }
      // Fallback: our own node, SAME @id as the template so they still merge.
      var pageUrl = location.origin + location.pathname;
      var ld = { '@context': 'https://schema.org', '@type': 'Product',
        '@id': pageUrl + '#product', url: pageUrl,
        name: (window.PDP && window.PDP.title) || 'AURALIGN',
        brand: { '@type': 'Brand', name: 'AURALIGN' },
        aggregateRating: rating };
      if (review) ld.review = review;
      var existing = document.getElementById('alr-jsonld'); if (existing) existing.remove();
      var sc = document.createElement('script');
      sc.type = 'application/ld+json'; sc.id = 'alr-jsonld';
      sc.textContent = JSON.stringify(ld);
      document.head.appendChild(sc);
    } catch (e) { if (window.console) console.warn('[reviews] json-ld inject failed', e); }
  }
})();
